import { action, computed, observable, reaction } from 'mobx'
import {
  IUberEatsAlertTiming,
  IUberEatsDenyReason,
  IUberEatsIncomingOrderEntry,
  IUberEatsNewOrderPause,
  IUberEatsSavedQueue,
  IUberEatsTimerConfig,
  UberEatsDenyReasonCode,
  UberEatsIncomingOrderStatus,
  UberEatsNewOrderPauseOption,
  UberEatsOrderAction,
  UberEatsUrgencyStage,
} from '../models/UberEats/UberEatsModels'
import {
  IUberEatsOrderService,
  createUberEatsOrderService,
} from '../utilities/uberEatsOrderService'
import { UBER_EATS_DEFAULT_ALERT_TIMING, UBER_EATS_TIMER_CONFIG } from '../constants/UberEats'
import { clampAlertTiming } from '../utilities/uberEatsAlertTiming'
import { parseErrorMsg } from '../utilities/helpers'
import {
  EMPTY_SAVED_QUEUE,
  getNewOrderPauseUntil,
  getTimeRemainingMs,
  getUrgencyLabel,
  getUrgencyStage,
  isCoveredByNewOrderPause,
  isDenyDraftComplete,
  isExpirable,
  isNewOrderPauseActive,
  isValidDenyReasonCode,
  normalizeIncomingOrder,
  toDenyReason,
} from '../utilities/uberEatsIncomingOrder'
import { TimerId } from '../types/timers'

export interface IUberEatsStoreEnvironment {
  /** canUseUberEats && this device is the Uber Eats Order Alert Device; read on every access */
  isAlertDevice: () => boolean
  loadAlertTiming: () => IUberEatsAlertTiming
  saveAlertTiming: (timing: IUberEatsAlertTiming) => void
  loadNewOrderPause: () => IUberEatsNewOrderPause | null
  saveNewOrderPause: (pause: IUberEatsNewOrderPause | null) => void
  /** The queue saved before the last page reload, for this device only */
  loadIncomingQueue: () => IUberEatsSavedQueue
  saveIncomingQueue: (queue: IUberEatsSavedQueue) => void
  /** Resolves false when the browser blocked playback; must not reject */
  playChime: () => Promise<boolean>
  /** Unlocks audio from a user gesture; resolves false when still blocked */
  primeChime: () => Promise<boolean>
  notifySuccess: (message: string) => void
  notifyError: (message: string) => void
  now: () => number
}

export interface IUberEatsUrgency {
  stage: UberEatsUrgencyStage
  remainingMs: number
  label: string
}

export const createDetachedUberEatsEnvironment = (): IUberEatsStoreEnvironment => ({
  isAlertDevice: () => false,
  loadAlertTiming: () => UBER_EATS_DEFAULT_ALERT_TIMING,
  saveAlertTiming: () => undefined,
  loadNewOrderPause: () => null,
  saveNewOrderPause: () => undefined,
  loadIncomingQueue: () => EMPTY_SAVED_QUEUE,
  saveIncomingQueue: () => undefined,
  playChime: async () => true,
  primeChime: async () => true,
  notifySuccess: () => undefined,
  notifyError: () => undefined,
  now: Date.now,
})

const IN_FLIGHT_STATUS: Record<UberEatsOrderAction, UberEatsIncomingOrderStatus> = {
  accept: 'accepting',
  deny: 'denying',
}

const MAX_REMEMBERED_DECISIONS = 200

const BLOCKED_STATUSES: UberEatsIncomingOrderStatus[] = ['accepting', 'denying', 'expired']

/**
 * Single-device queue of Uber Eats orders awaiting accept/deny.
 * Each order carries its own status so acting on one never disturbs another.
 *
 * Every backend touchpoint stays behind IUberEatsOrderService (accept/deny) and
 * receiveOrder/removeIncomingOrder (push feed).
 */
export class UberEatsStore {
  /** Oldest first: index 0 is the order shown in the alert bar */
  @observable incomingOrders: IUberEatsIncomingOrderEntry[] = []

  @observable now: number

  @observable alertTiming: IUberEatsAlertTiming

  @observable newOrderPause: IUberEatsNewOrderPause | null

  /** True after the browser refused to play the chime; cleared by the next successful play */
  @observable soundBlocked = false

  /** Raised by "View Orders"; the Order Hub consumes it by opening the Incoming tab */
  @observable openIncomingTabRequested = false

  private tickerId: TimerId | undefined

  private lastChimeAt = 0

  private disposed = false

  @observable private decidedOrderIds: string[] = []

  private stopSavingIncomingOrders: () => void

  constructor(
    readonly orderService: IUberEatsOrderService = createUberEatsOrderService(),
    private env: IUberEatsStoreEnvironment = createDetachedUberEatsEnvironment(),
    readonly timer: IUberEatsTimerConfig = UBER_EATS_TIMER_CONFIG,
  ) {
    this.now = env.now()
    this.alertTiming = env.loadAlertTiming()
    this.newOrderPause = env.loadNewOrderPause()
    const saved = env.loadIncomingQueue()
    this.incomingOrders = [...saved.entries].sort((a, b) => a.order.receivedAt - b.order.receivedAt)
    this.decidedOrderIds = saved.decidedOrderIds
    this.expireOverdueOrders()
    this.stopSavingIncomingOrders = reaction(
      () => ({ entries: this.incomingOrders, decidedOrderIds: this.decidedOrderIds }),
      (queue) => env.saveIncomingQueue(queue),
    )
  }

  @computed get topOrder(): IUberEatsIncomingOrderEntry | undefined {
    return this.incomingOrders.length > 0 ? this.incomingOrders[0] : undefined
  }

  @computed get moreWaitingCount(): number {
    return Math.max(0, this.incomingOrders.length - 1)
  }

  @computed get isAlertDevice(): boolean {
    return this.env.isAlertDevice()
  }

  @computed get isNewOrderPauseActive(): boolean {
    return isNewOrderPauseActive(this.newOrderPause, this.now)
  }

  @computed get alertableOrders(): IUberEatsIncomingOrderEntry[] {
    const pause = this.newOrderPause
    return this.incomingOrders.filter(
      (entry) => !isCoveredByNewOrderPause(entry.order, pause, this.now),
    )
  }

  @computed get shouldShowAlertBar(): boolean {
    return this.isAlertDevice && this.alertableOrders.length > 0
  }

  @computed get incomingBadgeCount(): number {
    return this.isAlertDevice ? this.incomingOrders.length : 0
  }

  getUrgency = (entry: IUberEatsIncomingOrderEntry): IUberEatsUrgency => {
    const remainingMs = getTimeRemainingMs(entry.order.receivedAt, this.now, this.timer.slaMs)
    const stage = getUrgencyStage(remainingMs, this.alertTiming)
    return { stage, remainingMs, label: getUrgencyLabel(stage, remainingMs) }
  }

  /** Accepts an untrusted feed payload; payloads that cannot be tracked are logged and dropped. */
  @action
  receiveOrder = (payload: unknown) => {
    const order = normalizeIncomingOrder(payload)
    if (!order) {
      console.warn('Ignoring Uber Eats order without a usable id or arrival time', payload)
      return
    }
    if (this.findEntry(order.id) || this.decidedOrderIds.includes(order.id)) return

    this.incomingOrders = [...this.incomingOrders, { order, status: 'pending' as const }].sort(
      (a, b) => a.order.receivedAt - b.order.receivedAt,
    )
    this.now = this.env.now()
    this.expireOverdueOrders()

    const received = this.findEntry(order.id)
    const shouldChime =
      this.isAlertDevice &&
      received?.status === 'pending' &&
      !isCoveredByNewOrderPause(order, this.newOrderPause, this.now)
    if (shouldChime) this.chimeUnlessJustRung()

    this.ensureTicker()
  }

  @action
  removeIncomingOrder = (orderId: string) => {
    this.incomingOrders = this.incomingOrders.filter((entry) => entry.order.id !== orderId)
    this.ensureTicker()
  }

  @action
  clearIncomingOrders = () => {
    this.incomingOrders = []
    this.lastChimeAt = 0
    this.ensureTicker()
  }

  acceptOrder = (orderId: string) =>
    this.submit(orderId, 'accept', () => this.orderService.acceptOrder(orderId))

  denyOrder = (orderId: string, reason: IUberEatsDenyReason) => {
    if (!isValidDenyReasonCode(reason?.code)) return Promise.resolve(false)

    return this.submit(orderId, 'deny', () => this.orderService.denyOrder(orderId, reason))
  }

  @action
  startDeny = (orderId: string) => {
    const entry = this.findEntry(orderId)
    if (!entry || entry.status === 'expired') return

    this.updateEntry(orderId, {
      denyDraft: entry.denyDraft ?? { reasonCode: null, explanation: '' },
    })
  }

  @action
  cancelDeny = (orderId: string) => {
    this.updateEntry(orderId, { denyDraft: undefined })
  }

  @action
  setDenyReason = (orderId: string, reasonCode: UberEatsDenyReasonCode) => {
    const draft = this.findEntry(orderId)?.denyDraft
    if (!draft) return

    this.updateEntry(orderId, { denyDraft: { ...draft, reasonCode } })
  }

  @action
  setDenyExplanation = (orderId: string, explanation: string) => {
    const draft = this.findEntry(orderId)?.denyDraft
    if (!draft) return

    this.updateEntry(orderId, { denyDraft: { ...draft, explanation } })
  }

  confirmDeny = async (orderId: string) => {
    const draft = this.findEntry(orderId)?.denyDraft
    if (!draft || !isDenyDraftComplete(draft)) return false

    return this.denyOrder(orderId, toDenyReason(draft))
  }

  @action
  pauseNewOrders = (option: UberEatsNewOrderPauseOption) => {
    const startedAt = this.env.now()
    this.now = startedAt
    this.storePause({ startedAt, until: getNewOrderPauseUntil(option, startedAt) })
    this.ensureTicker()
  }

  @action
  resumeNewOrders = () => {
    this.storePause(null)
    this.ensureTicker()
  }

  @action
  setAlertTiming = (timing: IUberEatsAlertTiming) => {
    this.alertTiming = clampAlertTiming(timing)
    this.env.saveAlertTiming(this.alertTiming)
  }

  restoreTicker = () => this.ensureTicker()

  /** Resolves true when playback was unlocked. */
  primeSound = async () => {
    const primed = await this.env.primeChime()
    this.setSoundBlocked(!primed)
    return primed
  }

  @action
  requestOpenIncomingTab = () => {
    this.openIncomingTabRequested = true
  }

  @action
  clearOpenIncomingTabRequest = () => {
    this.openIncomingTabRequested = false
  }

  /** Stops every timer for good; later calls never restart the ticker. */
  dispose = () => {
    this.disposed = true
    this.stopSavingIncomingOrders()
    this.ensureTicker()
  }

  private submit = async (
    orderId: string,
    action: UberEatsOrderAction,
    request: () => Promise<void>,
  ) => {
    const entry = this.findEntry(orderId)
    if (!entry || BLOCKED_STATUSES.includes(entry.status)) return false

    const { displayId, customerName } = entry.order
    this.updateEntry(orderId, {
      status: IN_FLIGHT_STATUS[action],
      errorMessage: undefined,
      failedAction: undefined,
    })

    try {
      await request()
    } catch (error) {
      if (this.findEntry(orderId)) this.failEntry(orderId, action, displayId, parseErrorMsg(error))
      return false
    }

    this.rememberDecision(orderId)
    if (!this.findEntry(orderId)) return true

    this.removeIncomingOrder(orderId)
    this.env.notifySuccess(
      action === 'accept'
        ? `Accepted #${displayId} (${customerName}) — it will appear in Online Orders`
        : `Denied #${displayId}`,
    )
    return true
  }

  // Stay in the queue so staff see the failure instead of assuming Uber got it
  private failEntry(
    orderId: string,
    failedAction: UberEatsOrderAction,
    displayId: string,
    reason: string | undefined,
  ) {
    const errorMessage = reason || 'Request to Uber Eats failed'
    this.updateEntry(orderId, { status: 'failed', failedAction, errorMessage })
    this.env.notifyError(
      failedAction === 'accept'
        ? `Uber Eats order #${displayId} was NOT accepted: ${errorMessage}`
        : `Uber Eats order #${displayId} was NOT denied: ${errorMessage}`,
    )
  }

  @action
  private rememberDecision(orderId: string) {
    this.decidedOrderIds = [...this.decidedOrderIds, orderId].slice(-MAX_REMEMBERED_DECISIONS)
  }

  private findEntry(orderId: string) {
    return this.incomingOrders.find((entry) => entry.order.id === orderId)
  }

  @action
  private updateEntry(orderId: string, patch: Partial<IUberEatsIncomingOrderEntry>) {
    this.incomingOrders = this.incomingOrders.map((entry) =>
      entry.order.id === orderId ? { ...entry, ...patch } : entry,
    )
  }

  @action
  private storePause(pause: IUberEatsNewOrderPause | null) {
    this.newOrderPause = pause
    this.env.saveNewOrderPause(pause)
  }

  @action
  private setSoundBlocked(blocked: boolean) {
    this.soundBlocked = blocked
  }

  @action
  private tick = () => {
    this.now = this.env.now()
    this.expireOverdueOrders()

    if (this.newOrderPause && !isNewOrderPauseActive(this.newOrderPause, this.now)) {
      this.storePause(null)
    }

    this.maybeRepeatUrgentChime()
    this.ensureTicker()
  }

  // TODO: GR-14015 — client-side expiry until a server service exists
  @action
  private expireOverdueOrders() {
    const isOverdue = (entry: IUberEatsIncomingOrderEntry) =>
      isExpirable(entry) &&
      getTimeRemainingMs(entry.order.receivedAt, this.now, this.timer.slaMs) === 0

    if (!this.incomingOrders.some(isOverdue)) return

    this.incomingOrders = this.incomingOrders.map((entry) =>
      isOverdue(entry) ? { ...entry, status: 'expired' as const } : entry,
    )
  }

  private maybeRepeatUrgentChime() {
    if (!this.shouldShowAlertBar) return
    if (this.now - this.lastChimeAt < this.timer.urgentChimeRepeatMs) return

    const hasUrgentOrder = this.alertableOrders.some(
      (entry) => entry.status !== 'expired' && this.getUrgency(entry).stage === 'urgent',
    )
    if (hasUrgentOrder) this.chime()
  }

  // A burst of orders (feed hydration) rings once, not once per order
  private chimeUnlessJustRung() {
    if (this.lastChimeAt !== 0 && this.now - this.lastChimeAt < this.timer.tickMs) return

    this.chime()
  }

  private chime() {
    this.lastChimeAt = this.now
    void this.ring()
  }

  private async ring() {
    try {
      this.setSoundBlocked(!(await this.env.playChime()))
    } catch {
      this.setSoundBlocked(true)
    }
  }

  private ensureTicker() {
    const shouldTick =
      !this.disposed &&
      (this.incomingOrders.some((entry) => entry.status !== 'expired') || !!this.newOrderPause)

    if (shouldTick && this.tickerId === undefined) {
      this.tickerId = setInterval(this.tick, this.timer.tickMs)
    }
    if (!shouldTick && this.tickerId !== undefined) {
      clearInterval(this.tickerId)
      this.tickerId = undefined
    }
  }
}
